/**
 * Charts in the app (SPEC §7.9): where a stack lives (a tab, or the Done tab), reading and
 * saving its charts, what a tap on a piece narrows the list to, and the shapes the styles draw.
 */
import { useMemo, useSyncExternalStore } from 'react'
import { doneTabCharts, normalizeView, type ChartColor, type ChartCtx, type ChartSpec, type SavedView, type Tint } from '@terrazzo/core'
import { store } from '../store/store'
import { useFields, useHousehold, useToday, useUsers } from '../store/hooks'
import { deviceZone } from './clock'

/** Where a stack of charts lives: a tab, or the Done tab. */
export type ChartHome = { kind: 'tab'; id: string } | { kind: 'done' }
export const homeKey = (h: ChartHome) => (h.kind === 'tab' ? h.id : 'done')
export const homeOf = (key: string): ChartHome => (key === 'done' ? { kind: 'done' } : { kind: 'tab', id: key })

export function viewOf(id: string): SavedView | undefined {
  const raw = store.get(id)?.props.view as SavedView | undefined
  return raw ? normalizeView(raw) : undefined
}

/** The card a tab is about (one card in its Shows): where a Trail or Metro leads. */
export function goalOf(viewId: string): string | null {
  const v = viewOf(viewId)
  return v?.filter.ancestors?.length === 1 ? v.filter.ancestors[0]! : null
}

/** A stack's charts, first to last. */
export function chartsOf(h: ChartHome): ChartSpec[] {
  if (h.kind === 'done') return doneTabCharts(store.household().doneCharts)
  return (store.get(h.id)?.props.view as SavedView | undefined)?.charts ?? []
}

/** Ideas already seen or turned down there (idea key → how many items it had then). */
export function ideasSeenOf(h: ChartHome): Record<string, number> {
  if (h.kind === 'done') return store.household().doneChartIdeas
  return (store.get(h.id)?.props.view as SavedView | undefined)?.chartIdeas ?? {}
}

/** What tapping a piece narrows the list below to (a capsule clears it). */
export interface Narrow {
  /** The chart and piece it came from. */
  chart: string
  key: string
  label: string
  ids: ReadonlySet<string>
  /** A date piece: only what happened in it (the Done tab). */
  from?: string
  to?: string
}

/** Everything a chart is worked out from, besides its items. */
export function useChartCtx(tints?: Readonly<Record<string, Tint>>): ChartCtx {
  const today = useToday()
  const hh = useHousehold()
  const fields = useFields()
  const users = useUsers()
  const tz = deviceZone()
  return useMemo(() => ({ today, tz, weekStart: hh.weekStart, fields, users, ...(tints ? { tints } : {}) }), [today, tz, hh.weekStart, fields, users, tints])
}

/** A color solid (what's done); hatched in it (what's still open or planned, SPEC §7.10); text on it. */
export const solid = (c: ChartColor) => `var(--c-${c})`
export const dim = (c: ChartColor) => `url(#tz-hatch-${c})`
export const onColor = (c: ChartColor) => `var(--c-${c}-on)`

// ---------------------------------------------------------------------------
// A stack's place on screen, shared by its toolbar button and the stack itself

/** `jump`: where to go next, its last page (an idea, New chart) or a chart (by id); `n` counts asks. */
type StackUi = { open: boolean; jump: string | null; n: number }
const ui = new Map<string, StackUi>()
const subs = new Set<() => void>()
const emit = () => subs.forEach((f) => f())
const CLOSED: StackUi = { open: false, jump: null, n: 0 }

export const stackUi = {
  get: (key: string): StackUi => ui.get(key) ?? CLOSED,
  /** Show the stack (even with no charts yet) and go to its last page (an idea, New chart). */
  show(key: string) {
    const cur = ui.get(key) ?? CLOSED
    ui.set(key, { open: true, jump: END_PAGE, n: cur.n + 1 })
    emit()
  },
  /** Go to a chart (one just added). */
  goTo(key: string, chart: string) {
    const cur = ui.get(key) ?? CLOSED
    chartPage.set(key, chart)
    ui.set(key, { ...cur, jump: chart, n: cur.n + 1 })
    emit()
  },
  hide(key: string) {
    ui.set(key, CLOSED)
    emit()
  },
  subscribe(fn: () => void) {
    subs.add(fn)
    return () => subs.delete(fn)
  },
}

export function useStackUi(key: string): StackUi {
  return useSyncExternalStore(stackUi.subscribe, () => stackUi.get(key))
}

/** The page each stack is on (its dots, in the row above it, show it). */
const at = new Map<string, number>()
export const stackPage = {
  set(key: string, i: number) {
    if (at.get(key) === i) return
    at.set(key, i)
    emit()
  },
}
export function useStackPage(key: string): number {
  return useSyncExternalStore(stackUi.subscribe, () => at.get(key) ?? 0)
}

/** The stack's last page: Add a chart (what's recommended there, and New chart). */
export const END_PAGE = 'end'

/** The chart each stack was last on (on this phone), so it opens there again. */
const PAGE_KEY = 'tz.chartPage'
let pages: Record<string, string> | null = null
export const chartPage = {
  get(key: string): string | undefined {
    if (!pages) {
      try {
        pages = JSON.parse(localStorage.getItem(PAGE_KEY) ?? '{}') as Record<string, string>
      } catch {
        pages = {}
      }
    }
    return pages[key]
  },
  set(key: string, chart: string) {
    if (chartPage.get(key) === chart) return
    pages![key] = chart
    try {
      localStorage.setItem(PAGE_KEY, JSON.stringify(pages))
    } catch {
      // private mode: this session only
    }
  },
}

// ---------------------------------------------------------------------------
// Shapes

/**
 * A ring segment with small rounded corners (the logo's quarter ring): angles clockwise from
 * the top, in radians. Drawn inset and stroked round, so the corners are round.
 */
export function sectorPath(r0: number, r1: number, a0: number, a1: number, rc: number): string | null {
  const R1 = r1 - rc
  const R0 = r0 + rc
  if (R1 <= R0) return null
  const s1 = a0 + rc / R1
  const e1 = a1 - rc / R1
  const s0 = a0 + rc / R0
  const e0 = a1 - rc / R0
  if (e1 <= s1 || e0 <= s0) return null
  const p = (r: number, a: number) => `${(Math.sin(a) * r).toFixed(2)} ${(-Math.cos(a) * r).toFixed(2)}`
  const big1 = e1 - s1 > Math.PI ? 1 : 0
  const big0 = e0 - s0 > Math.PI ? 1 : 0
  return `M${p(R1, s1)}A${R1} ${R1} 0 ${big1} 1 ${p(R1, e1)}L${p(R0, e0)}A${R0} ${R0} 0 ${big0} 0 ${p(R0, s0)}Z`
}

export interface Rect<T> {
  x: number
  y: number
  w: number
  h: number
  item: T
}

/** Tiles packed into a rectangle, each sized by its value, as close to square as they'll go. */
export function squarify<T>(items: Array<{ value: number; item: T }>, x: number, y: number, w: number, h: number): Array<Rect<T>> {
  const total = items.reduce((a, b) => a + b.value, 0)
  if (!total || w <= 0 || h <= 0) return []
  const scale = (w * h) / total
  const nodes = items.filter((i) => i.value > 0).map((i) => ({ a: i.value * scale, item: i.item }))
  const out: Array<Rect<T>> = []
  let rect = { x, y, w, h }
  let row: typeof nodes = []
  const worst = (r: typeof nodes, len: number) => {
    const s = r.reduce((a, b) => a + b.a, 0)
    const mx = Math.max(...r.map((n) => n.a))
    const mn = Math.min(...r.map((n) => n.a))
    return Math.max((len * len * mx) / (s * s), (s * s) / (len * len * mn))
  }
  const layout = (r: typeof nodes) => {
    const s = r.reduce((a, b) => a + b.a, 0)
    if (rect.w >= rect.h) {
      const cw = s / rect.h
      let yy = rect.y
      for (const n of r) {
        const hh = n.a / cw
        out.push({ x: rect.x, y: yy, w: cw, h: hh, item: n.item })
        yy += hh
      }
      rect = { x: rect.x + cw, y: rect.y, w: rect.w - cw, h: rect.h }
    } else {
      const rh = s / rect.w
      let xx = rect.x
      for (const n of r) {
        const ww = n.a / rh
        out.push({ x: xx, y: rect.y, w: ww, h: rh, item: n.item })
        xx += ww
      }
      rect = { x: rect.x, y: rect.y + rh, w: rect.w, h: rect.h - rh }
    }
  }
  let i = 0
  while (i < nodes.length) {
    const len = Math.min(rect.w, rect.h)
    const n = nodes[i]!
    if (!row.length || worst([...row, n], len) <= worst(row, len)) {
      row.push(n)
      i++
    } else {
      layout(row)
      row = []
    }
  }
  if (row.length) layout(row)
  return out
}

/** A smooth line through points (Catmull-Rom as curves). */
export function smoothPath(pts: Array<[number, number]>): string {
  if (!pts.length) return ''
  let d = `M${pts[0]![0].toFixed(1)} ${pts[0]![1].toFixed(1)}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)]!
    const p1 = pts[i]!
    const p2 = pts[i + 1]!
    const p3 = pts[Math.min(pts.length - 1, i + 2)]!
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6]
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6]
    d += `C${c1[0]!.toFixed(1)} ${c1[1]!.toFixed(1)} ${c2[0]!.toFixed(1)} ${c2[1]!.toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`
  }
  return d
}

/** One formatter per zone (making one is slow, and the time strip asks on every slide). */
const hereFormat = new Map<string, Intl.DateTimeFormat>()

/** This phone's time now, in minutes from midnight, and its date. */
export function nowHere(): { date: string; minutes: number } {
  const zone = deviceZone()
  let f = hereFormat.get(zone)
  if (!f) hereFormat.set(zone, (f = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })))
  const parts = f.formatToParts(new Date())
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  return { date: `${get('year')}-${get('month')}-${get('day')}`, minutes: Number(get('hour')) * 60 + Number(get('minute')) }
}

/** "in 25 min", "in 1 hr 10", "now". */
export function inHowLong(minutes: number): string {
  if (minutes <= 0) return 'now'
  if (minutes < 60) return `in ${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m ? `in ${h} hr ${m}` : `in ${h} hr`
}

/** "45 min", "1 hr 15", "2 hr". */
export function howLong(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m ? `${h} hr ${m}` : `${h} hr`
}

/** Minutes from midnight as a clock time ("9:30", "12 PM" style via formatTime). */
export const minutesToTime = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

/** Everything that's been checked off (what the Done tab's charts read, before its filters). */
export function doneItemIds(): string[] {
  const out: string[] = []
  for (const it of store.snaps.values()) if (!store.tree.inTrash.has(it.id) && (it.completions.length || it.done === true)) out.push(it.id)
  return out
}
